import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "../../contexts/AuthContext";
import getApiInstance from "../../services/api";
import { DateTimeToBr } from "../../hooks/Utils";
import DateRangeSelector from "../../components/ui/DateRangeSelector";
import { format, subDays } from "date-fns";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import TransactionDetails from "../../components/TransactionDetails";

export default function TransactionsHistory() {
  const { system } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [expanded, setExpanded] = useState({});

  // Formulário de busca
  const [searchForm, setSearchForm] = useState({
    dateRange: {
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
    },
    cpf: "",
    idCobranca: "",
    status: "",
  });

  const handleExpandClick = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Busca transações
  const handleSearch = async () => {
    try {
      setLoading(true);
      setError("");
      setTransactions([]);

      const api = getApiInstance(system);
      const payload = {
        dateRange: {
          startDate: format(searchForm.dateRange.startDate, "yyyy-MM-dd"),
          endDate: format(searchForm.dateRange.endDate, "yyyy-MM-dd"),
        },
      };

      const response = await api.post("/transactions", payload);
      setTransactions(response.data);
    } catch (err) {
      setError(
        "Erro ao buscar transações: " +
          (err.response?.data?.message || err.message)
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Cancela transação
  const handleCancelTransaction = async (id) => {
    try {
      setLoading(true);
      const api = getApiInstance(system);
      await api.delete(`/payment/cancelRequest/${id}`);
      handleSearch();
    } catch (err) {
      setError("Erro ao cancelar transação.");
    } finally {
      setLoading(false);
    }
  };

  // --- MOBILE (Lista) ---
  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Histórico de Transações
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DateRangeSelector
              startDate={searchForm.dateRange.startDate}
              endDate={searchForm.dateRange.endDate}
              onChange={(dateRange) =>
                setSearchForm((prev) => ({ ...prev, dateRange }))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
              fullWidth
            >
              {loading ? <CircularProgress size={24} /> : "Listar"}
            </Button>
          </Grid>
        </Grid>
      </Paper>
      {transactions.length > 99 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {`Exibindo os primeiros ${transactions.length} resultados - refine sua busca`}
        </Alert>
      )}

      <List>
        {transactions.map((tx) => (
          <Box key={tx.Id}>
            <ListItem
              secondaryAction={
                <IconButton edge="end" onClick={() => handleExpandClick(tx.Id)}>
                  {expanded[tx.Id] ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
            >
              <ListItemText
                sx={{ fontWeight: "bolder" }}
                primary={`${DateTimeToBr(tx.Data)} - ${tx.Nome}`}
                secondary={
                  <>
                    <span>
                      <strong>CPF:</strong> {tx.CPF} |{" "}
                      <strong>IDCobranca:</strong> {tx.IDCobranca}
                    </span>
                    <br />
                    <span>
                      <strong>Tipo:</strong> {tx.Pagamento?.Name || tx.Forma} |{" "}
                      <strong>Valor:</strong>{" "}
                      {tx.Valor.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })}{" "}
                      | <strong>Status:</strong> {tx.Status}
                      {tx.Status === "Pendente" && (
                        <Button
                          color="error"
                          size="small"
                          sx={{ ml: 2 }}
                          onClick={() => handleCancelTransaction(tx.Id)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </span>
                  </>
                }
              />
            </ListItem>
            <Collapse in={expanded[tx.Id]} timeout="auto" unmountOnExit>
              <TransactionDetails transaction={tx} />
            </Collapse>
          </Box>
        ))}
      </List>
    </Box>
  );
}
